import { supabase } from './supabase';

export type ArchiveFile = { name: string; bytes: Uint8Array };
const encode = (text: string) => new TextEncoder().encode(text);

// Standard uncompressed ZIP. No external service receives the exported records.
export function makeZip(files: ArchiveFile[]): Blob {
  const chunks: Uint8Array[] = [], directory: Uint8Array[] = [];
  let offset = 0, directorySize = 0;
  for (const file of files) {
    const name = encode(file.name);
    let crc = 0xffffffff;
    for (const byte of file.bytes) {
      crc ^= byte;
      for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
    crc = (crc ^ 0xffffffff) >>> 0;
    const header = new Uint8Array(30 + name.length), h = new DataView(header.buffer);
    h.setUint32(0, 0x04034b50, true); h.setUint16(4, 20, true); h.setUint16(6, 0x800, true);
    h.setUint16(12, 33, true); h.setUint32(14, crc, true);
    h.setUint32(18, file.bytes.length, true); h.setUint32(22, file.bytes.length, true); h.setUint16(26, name.length, true);
    header.set(name, 30); chunks.push(header, file.bytes);
    const entry = new Uint8Array(46 + name.length), d = new DataView(entry.buffer);
    d.setUint32(0, 0x02014b50, true); d.setUint16(4, 20, true); d.setUint16(6, 20, true);
    d.setUint16(8, 0x800, true); d.setUint16(14, 33, true); d.setUint32(16, crc, true);
    d.setUint32(20, file.bytes.length, true); d.setUint32(24, file.bytes.length, true);
    d.setUint16(28, name.length, true); d.setUint32(42, offset, true); entry.set(name, 46);
    directory.push(entry); directorySize += entry.length; offset += header.length + file.bytes.length;
  }
  const end = new Uint8Array(22), e = new DataView(end.buffer);
  e.setUint32(0, 0x06054b50, true); e.setUint16(8, files.length, true); e.setUint16(10, files.length, true);
  e.setUint32(12, directorySize, true); e.setUint32(16, offset, true);
  return new Blob([...chunks, ...directory, end] as BlobPart[], { type: 'application/zip' });
}

type Row = Record<string, unknown>;
export async function savePropertySnapshot(propertyId: string, propertyName: string, progress: (text: string) => void) {
  if (!supabase) throw new Error('Sign in to a connected account to save your property record.');
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) throw new Error('Your session has expired. Sign in again.');
  const startedAt = new Date().toISOString();
  const tables = ['properties', 'property_members', 'projects', 'leads', 'property_invitations', 'documents', 'property_data_sources', 'property_funds', 'property_fund_sources', 'property_fund_allocations', 'property_fund_entries', 'property_guild_contacts', 'property_knowledge_records', 'project_quote_requests', 'record_change_history'];
  const records: Record<string, Row[]> = {}, omissions: string[] = [];
  const files: ArchiveFile[] = [];
  for (const table of tables) {
    progress(`Reading ${table.replaceAll('_', ' ')}…`);
    records[table] = [];
    for (let start = 0; ; start += 100) {
      const { data, error } = await supabase.from(table).select('*').eq(table === 'properties' ? 'id' : 'property_id', propertyId).order('id').range(start, start + 99);
      if (error) { omissions.push(`${table}: ${error.message}`); break; }
      records[table].push(...(data ?? []));
      if (!data || data.length < 100) break;
    }
    if (table === 'properties' && !records.properties.length) throw new Error('The property could not be read. No snapshot was created.');
  }
  let totalBytes = 0;
  const documents: Array<{ id: unknown; archivePath?: string; sha256?: string; size?: number; error?: string }> = [];
  for (const row of records.documents) {
    progress(`Downloading document ${documents.length + 1} of ${records.documents.length}…`);
    try {
      if (totalBytes >= 100 * 1024 * 1024) throw new Error('100 MB export limit reached; download this document separately.');
      const { data, error } = await supabase.storage.from('property-documents').download(String(row.storage_path));
      if (error || !data) throw new Error(error?.message ?? 'Document could not be downloaded.');
      if (totalBytes + data.size > 100 * 1024 * 1024) throw new Error('100 MB export limit exceeded; download this document separately.');
      const bytes = new Uint8Array(await data.arrayBuffer());
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      const sha256 = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
      const basename = String(row.storage_path).split('/').pop()!.replace(/[^a-zA-Z0-9._-]/g, '_');
      const archivePath = `documents/${row.id}-${basename}`;
      files.push({ name: archivePath, bytes }); totalBytes += bytes.length;
      documents.push({ id: row.id, archivePath, sha256, size: bytes.length });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Document download failed.';
      omissions.push(`Document ${row.id}: ${message}`); documents.push({ id: row.id, error: message });
    }
  }
  const completedAt = new Date().toISOString();
  files.push({ name: 'property-record.json', bytes: encode(JSON.stringify({ schemaVersion: 1, propertyId, startedAt, completedAt, records }, null, 2)) });
  files.push({ name: 'manifest.json', bytes: encode(JSON.stringify({ version: '0.9.0', propertyId, propertyName, startedAt, completedAt, status: omissions.length ? 'partial' : 'completed', scope: 'Records readable by this signed-in owner in listed tables; document binaries where permitted. Other account records, external communications, billing and unlisted tables are not included.', counts: Object.fromEntries(Object.entries(records).map(([name, rows]) => [name, rows.length])), documents, omissions }, null, 2)) });
  files.push({ name: 'READ_ME.txt', bytes: encode('Growth Rings Property record export\n\nPrivate: may contain addresses, contacts, invitations and financial records. Store securely.\n\nCheck manifest.json for omissions and scope. This export is not a certified record, complete account backup, atomic database snapshot, write-protected archive, or automatic restore package. It was read over the stated time window; simultaneous changes may occur. Document SHA-256 hashes help compare bytes but do not prove authorship or trusted time. No login credentials or signed download URLs are included.\n') });
  progress('Preparing download…');
  const blob = makeZip(files), url = URL.createObjectURL(blob);
  const link = document.createElement('a'); link.href = url;
  link.download = `growth-rings-${propertyName.replace(/[^a-zA-Z0-9-]/g, '-').slice(0, 60)}-${completedAt.replace(/[:.]/g, '-')}.zip`;
  document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { omissions, documents: documents.filter(item => item.archivePath).length };
}
